import { useState } from "react";
import { NAVY } from "../styles/palette";
import { getPerVisit, DEO_PER_VISIT } from "../lib/pricing";
import { SKIP_REASONS, CREDIT_REASONS, skipReasonLabel, SKIP_NOTE_MAX } from "../lib/constants";


// `mode` controls which flow this is:
//   "skip"   — visit didn't happen. Status → skipped, credit optional.
//   "credit" — visit happened but something went wrong. Status stays
//              completed, credit is the whole point (forced on).
export function SkipVisitModal({ visit, client, pricing, onClose, onSave, mode = "skip" }) {
  const isCreditMode = mode === "credit";
  const reasonList   = isCreditMode ? CREDIT_REASONS : SKIP_REASONS;
  const [reason, setReason] = useState(reasonList[0]);
  // In credit mode the credit checkbox is always on (issuing it is the
  // point); in skip mode it defaults off so a routine skip doesn't
  // unintentionally refund the client.
  const [note, setNote] = useState("");
  const [credit, setCredit] = useState(isCreditMode);
  const [saving, setSaving] = useState(false);
  // B10: show the rate for the service actually being skipped (deo vs cleanup).
  const svcType  = visit?.service_type || visit?.serviceType || "cleanup";
  const dFreq    = client.deo_frequency || "biweekly";
  const perVisit = svcType === "deo"
    ? (pricing?.deoPerVisit?.[dFreq] ?? DEO_PER_VISIT[dFreq] ?? 0)
    : getPerVisit(client.frequency, client.dogs, pricing);

  async function handleSave() {
    setSaving(true);
    await onSave(reason, credit, isCreditMode ? "" : note.trim());
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:420}}>
        <div className="modal-header">
          <h3>{isCreditMode ? "🎁 Issue Credit for Completed Visit" : "⊘ Mark Visit as Skipped"}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{background:"#f4f5f7",borderRadius:6,padding:"10px 14px",marginBottom:16,fontSize:13}}>
            <div style={{fontWeight:600,color:"#1a1f36"}}>{client.name}</div>
            <div style={{color:"#42526e",marginTop:2}}>Visit date: <strong>{visit.date || visit.scheduled_date}</strong>{visit.day ? ` (${visit.day})` : ""}</div>
            {isCreditMode && (
              <div style={{color:"#8993a4",marginTop:6,fontSize:12}}>
                The visit will stay marked <strong>completed</strong>. Only a credit is being issued.
              </div>
            )}
          </div>

          <div className="form-group" style={{marginBottom:14}}>
            <label>{isCreditMode ? "Reason for credit" : "Reason for skipping"}</label>
            <select value={reason} onChange={e=>setReason(e.target.value)}>
              {reasonList.map(r=><option key={r} value={r}>{isCreditMode ? r : skipReasonLabel(r)}</option>)}
            </select>
          </div>

          {!isCreditMode && (
            <div className="form-group" style={{marginBottom:14}}>
              <label>Skip note <span style={{color:"#8993a4",fontWeight:400}}>(optional)</span></label>
              <textarea value={note} onChange={e=>setNote(e.target.value)} maxLength={SKIP_NOTE_MAX} rows={2} placeholder="Anything the office should know"/>
            </div>
          )}

          {/* Credit checkbox — hidden in credit mode (always on) */}
          {!isCreditMode && (
            <div style={{background:"#fffae6",border:"1px solid #fde047",borderRadius:8,padding:"12px 14px"}}>
              <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",marginBottom:0}}>
                <input
                  type="checkbox"
                  checked={credit}
                  onChange={e=>setCredit(e.target.checked)}
                  style={{accentColor:NAVY,marginTop:2,width:16,height:16,flexShrink:0}}
                />
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"#1a1f36"}}>Apply credit to next invoice</div>
                  <div style={{fontSize:12,color:"#8993a4",marginTop:2}}>
                    Creates a <strong style={{color:"#006644"}}>-${perVisit.toFixed(2)}</strong> credit invoice (1 visit at ${perVisit.toFixed(2)}/visit).
                    This will offset their next monthly charge.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* In credit mode show a summary card instead of a checkbox */}
          {isCreditMode && (
            <div style={{background:"#e3fcef",border:"1px solid #b3e6c8",borderRadius:8,padding:"12px 14px"}}>
              <div style={{fontSize:13,fontWeight:600,color:"#065f46"}}>Credit Amount</div>
              <div style={{fontSize:12,color:"#065f46",marginTop:4}}>
                <strong style={{fontSize:18}}>-${perVisit.toFixed(2)}</strong> will be applied to {client.name}'s next invoice
                <span style={{color:"#8993a4",fontWeight:400}}> · 1 visit at ${perVisit.toFixed(2)}/visit</span>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving
              ? "Saving..."
              : isCreditMode ? "Issue Credit" : "Mark as Skipped"}
          </button>
        </div>
      </div>
    </div>
  );
}
